// ── Enums ─────────────────────────────────────────────────────────

export type UserRole =
  | 'cfo'
  | 'procurement_head'
  | 'procurement_manager'
  | 'procurement_executive'
  | 'member'
  | 'super_admin';

export type PrStatus =
  | 'draft'
  | 'submitted'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'ordered';

export type PrType = 'new_purchase' | 'renewal' | 'upgrade' | 'cancellation';

export type ApprovalStepStatus = 'pending' | 'approved' | 'rejected' | 'skipped' | 'escalated';

export type VendorStatus = 'active' | 'inactive' | 'blacklisted';
export type VendorCategory = 'saas' | 'hardware' | 'professional_services' | 'infrastructure' | 'other';

export type PoStatus = 'draft' | 'sent' | 'acknowledged' | 'completed' | 'cancelled';

// ── Entities ──────────────────────────────────────────────────────

export interface ClientTenant {
  id: string;
  name: string;
  slug: string;
  displayCurrency: string;
  region: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  tenantId: string;
  departmentId: string | null;
}

export interface Department {
  id: string;
  tenantId: string;
  name: string;
  code: string | null;
  budget: string | null;
  headUserId: string | null;
  isActive: boolean;
}

export interface Vendor {
  id: string;
  tenantId: string;
  name: string;
  category: VendorCategory;
  status: VendorStatus;
  website: string | null;
}

export interface VendorRegistryItem {
  id: string;
  name: string;
  category: VendorCategory;
  status: VendorStatus;
  website: string | null;
  renewalDate: string | null;
  daysUntilRenewal: number | null;
  product: string | null;
  currentPricePerUnit: number | null;
  currentVolume: number | null;
  annualContractValue: number | null;
  contractType: string | null;
  contractLengthMonths: number | null;
  paymentTerms: string | null;
  unit: string | null;
  benchmark: {
    runId: string;
    runStatus: string;
    volumeTier: string | null;
    listPriceAtVolume: number | null;
    marketMedianAtVolume: number | null;
    bestNegotiatedAtVolume: number | null;
    overpaymentPct: number | null;
    savingsPotentialUsd: number | null;
    volumeDiscountAvailable: boolean | null;
    negotiationLevers: string[];
    adjustedMarketMedian: number | null;
    adjustedSavingsUsd: number | null;
  } | null;
}

export interface SavingsDashboard {
  totalAssessedAcv: number;
  latestRun: {
    id: string;
    status: string;
    savingsLow: number | null;
    savingsHigh: number | null;
    totalAcv: number | null;
    vendorCount: number;
    completedCount: number;
    createdAt: string;
  } | null;
  topVendors: {
    vendorId: string | null;
    vendorName: string;
    product: string | null;
    currentAcv: number | null;
    overpaymentPct: number | null;
    savingsPotentialUsd: number;
  }[];
  spendByCategory: {
    category: string;
    totalAcv: number;
    vendorCount: number;
  }[];
  upcomingRenewals: {
    id: string;
    name: string;
    renewalDate: string;
    annualContractValue: number | null;
    daysUntilRenewal: number;
  }[];
  savingsDeliveredThisQuarter: number;
}

export interface PurchaseRequest {
  id: string;
  tenantId: string;
  requesterId: string;
  requester?: { id: string; name: string; email: string; role: UserRole };
  vendorId: string | null;
  vendor?: Vendor | null;
  departmentId: string | null;
  department?: Department | null;
  type: PrType;
  status: PrStatus;
  title: string;
  description: string | null;
  amount: string;
  currency: string;
  seats: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  businessCase: string | null;
  spendCategory: string | null;
  metadata: Record<string, any>;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  // Attached by backend for list views
  currentApprovalStep?: {
    id: string;
    stepOrder: number;
    approver: { id: string; name: string; email: string } | null;
    approverRole: string | null;
    status: string;
  } | null;
}

export interface DashboardStats {
  vendorCount: number;
  totalAcv: number;
}

export interface ApprovalStep {
  id: string;
  tenantId: string;
  purchaseRequestId: string;
  purchaseRequest?: PurchaseRequest;
  stepOrder: number;
  approverId: string | null;
  approver?: { id: string; name: string; email: string; role: UserRole } | null;
  approverRole: string | null;
  status: ApprovalStepStatus;
  comments: string | null;
  decidedAt: string | null;
  dueAt: string | null;
  createdAt: string;
}

export interface PurchaseOrder {
  id: string;
  tenantId: string;
  purchaseRequestId: string;
  vendorId: string | null;
  vendor?: Vendor | null;
  poNumber: string;
  status: PoStatus;
  amount: string;
  currency: string;
  issuedAt: string | null;
  lineItems: Array<{ description: string; qty: number; unit_price: number; total: number }>;
  createdAt: string;
}

// ── Form DTOs ──────────────────────────────────────────────────────

export interface CreatePurchaseRequestDto {
  title: string;
  type: PrType;
  amount: number;
  vendorId?: string;
  vendorName?: string;
  departmentId?: string;
  description?: string;
  businessCase?: string;
  spendCategory?: string;
  seats?: number;
  periodStart?: string;
  periodEnd?: string;
}

// ── UI helpers ─────────────────────────────────────────────────────

export const PR_TYPE_LABELS: Record<PrType, string> = {
  new_purchase: 'New Purchase',
  renewal: 'Renewal',
  upgrade: 'Upgrade',
  cancellation: 'Cancellation',
};

export const PR_STATUS_LABELS: Record<PrStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  ordered: 'Ordered',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  cfo: 'CFO',
  procurement_head: 'Procurement Head',
  procurement_manager: 'Procurement Manager',
  procurement_executive: 'Procurement Executive',
  member: 'Member',
  super_admin: 'Super Admin',
};

export const SPEND_CATEGORIES = [
  'Cybersecurity',
  'Cloud & Hosting',
  'Productivity',
  'Identity & Access',
  'Dev Tools',
  'HR & People',
  'CRM & Sales',
  'Analytics & BI',
  'Legal & Compliance',
  'Marketing',
  'AI & Machine Learning',
  'Communication',
  'Finance & ERP',
  'Other',
];

export const PR_STATUS_COLORS: Record<PrStatus, string> = {
  draft: '#64748b',
  submitted: '#0ea5e9',
  pending_approval: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  cancelled: '#64748b',
  ordered: '#8b5cf6',
};
